import { Search2Icon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  type MyParticipationRequestRowDto,
  type PortalMyBidDto,
  BidStatus,
  ParticipationRequestStatus,
  PortalAwardOutcomeDto,
  PortalEventContextType,
  useGetPortalMyBidsQuery,
  useListMyParticipationRequestsQuery,
} from "../../autogen/bff-api";
import { displayDateWithTime } from "../../common/formatting/displayDateWithTime";
import { participationStatusBadge } from "../../common/qualification/participationStatusBadge";
import type { TFunction } from "i18next";
import { useApiError } from "../../common/errors/useApiError";
import { momentLocaleFor } from "../../common/formatting/i18nFormatters";
import { usePortalEventUrls } from "../../common/sourcing-events/portal/usePortalEventUrls";
import { LifecycleFilterChips, type LifecycleFilter } from "../sourcing-events/portal/components/LifecycleFilterChips";
import { PortalLifecycleTag } from "../sourcing-events/portal/components/PortalLifecycleTag";
import { isDesignContest } from "../sourcing-events/buyer/wizard/utils/procedure";
import { getTimeLeftMeta } from "../sourcing-events/portal/utils/displayFormatters";
import { useCurrentMoment } from "../sourcing-events/portal/utils/useCurrentMoment";
import { BidStatusTag } from "../sourcing-event/BidStatusTag";
import { isInvitedCompetition } from "../sourcing-events/portal/isInvitedCompetition";
import {
  countByLifecycle,
  isCompetitionOpen,
  lifecycleFilterOf,
  myBidDeadlineText,
  myBidTagProps,
  sortByUrgency,
} from "./myBids";

const SEARCH_DEBOUNCE_MS = 300;

const isOpenDraft = (bid: PortalMyBidDto): boolean => bid.status === BidStatus.Draft && isCompetitionOpen(bid);

export const awardedToText = (bid: PortalMyBidDto, t: TFunction): string => {
  switch (bid.awardOutcome) {
    case PortalAwardOutcomeDto.Winner:
      return t("Your company");
    case PortalAwardOutcomeDto.Withdrawn:
      return t("The award has been withdrawn");
    case PortalAwardOutcomeDto.NotSelected:
    case PortalAwardOutcomeDto.Rejected:
      return bid.awardedTo?.length ? bid.awardedTo.join(", ") : t("Another supplier");
    default:
      return "—";
  }
};

const isStaleDraft = (row: MyParticipationRequestRowDto, now: number): boolean =>
  row.request.status === ParticipationRequestStatus.Draft &&
  row.requestDeadline != null &&
  new Date(row.requestDeadline).getTime() <= now;

export const participationRequestBadge = (
  row: MyParticipationRequestRowDto,
  t: TFunction,
  now: number,
): { colorScheme: string; label: string } => {
  const request = row.request;
  if (request.selectionOutcome != null && !request.selectionOutcome.selected) {
    return { colorScheme: "purple", label: t("Not selected") };
  }
  if (isStaleDraft(row, now)) {
    return { colorScheme: "gray", label: t("Not sent – the deadline has passed") };
  }
  if (request.status === ParticipationRequestStatus.Submitted) {
    return { colorScheme: "teal", label: t("Request to participate sent") };
  }
  const { colorScheme, labelKey } = participationStatusBadge(request.status, "Submitted");
  return { colorScheme, label: t(labelKey) };
};

const latestRequestPerEvent = (newestFirst: MyParticipationRequestRowDto[]): MyParticipationRequestRowDto[] =>
  newestFirst.filter((row, index) => newestFirst.findIndex((other) => other.eventId === row.eventId) === index);

const requestMatchesQuery = (row: MyParticipationRequestRowDto, query: string): boolean =>
  !query ||
  [row.eventTitle ?? "", row.buyerOrganizationName ?? ""].join(" ").toLowerCase().includes(query.toLowerCase());

const matchesQuery = (bid: PortalMyBidDto, query: string): boolean => {
  if (!query) return true;
  const haystack = [bid.event.title, bid.event.buyerName, bid.event.referenceId ?? ""].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const SupplierBidsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = momentLocaleFor(i18n.language);
  const now = useCurrentMoment().valueOf();
  const portalUrls = usePortalEventUrls();
  const { show } = useApiError();

  const { data, isLoading, error } = useGetPortalMyBidsQuery({});
  const bids = useMemo(() => data?.bids ?? [], [data]);
  const {
    data: requestsData,
    isLoading: requestsLoading,
    isError: requestsFailed,
    refetch: refetchRequests,
  } = useListMyParticipationRequestsQuery({});
  const requestsWithoutBid = useMemo(
    () =>
      latestRequestPerEvent(requestsData?.rows ?? []).filter((row) => !bids.some((bid) => bid.eventId === row.eventId)),
    [requestsData, bids],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [filter, setFilter] = useState<LifecycleFilter>("ALL");

  const searched = useMemo(() => bids.filter((bid) => matchesQuery(bid, query)), [bids, query]);
  const lifecycleCounts = useMemo(() => countByLifecycle(bids), [bids]);
  const visibleRequests = useMemo(
    () => (filter === "ALL" ? requestsWithoutBid.filter((row) => requestMatchesQuery(row, query)) : []),
    [requestsWithoutBid, filter, query],
  );
  const visible = useMemo(
    () =>
      sortByUrgency(filter === "ALL" ? searched : searched.filter((bid) => lifecycleFilterOf(bid) === filter)),
    [searched, filter]
  );

  const openBid = (bid: PortalMyBidDto) =>
    navigate(
      isInvitedCompetition(bid.event.contextType)
        ? portalUrls.privateBid.edit.go(bid.eventId, bid.bidId)
        : portalUrls.bid.edit.go(bid.eventId, bid.bidId).intro,
    );
  const openRow = (bid: PortalMyBidDto) =>
    isOpenDraft(bid) ? openBid(bid) : navigate(portalUrls.view.go(bid.eventId));
  const openRequest = (row: MyParticipationRequestRowDto) =>
    navigate(
      row.request.status === ParticipationRequestStatus.Draft && !isStaleDraft(row, now)
        ? portalUrls.request.edit.go(row.eventId, row.request.id).intro
        : portalUrls.view.go(row.eventId),
    );

  return (
    <>
      <Heading as="h1" size="lg">
        {t("My bids")}
      </Heading>
      <Text color="smMuted" mt="2">
        {t("Track the procurements you are bidding on")}
      </Text>

      <InputGroup size="lg" mt="6" mb="5">
        <InputLeftElement pointerEvents="none" h="full" display="flex" alignItems="center" justifyContent="center">
          <Search2Icon color="smTextDisabled" boxSize="4" />
        </InputLeftElement>
        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t("Search your bids") ?? ""}
          aria-label={t("Search your bids") ?? ""}
          bg="smBackground"
          borderColor="smInputBorder"
          rounded="xl"
          focusBorderColor="smPrimary"
        />
      </InputGroup>

      {error ? (
        show(error)
      ) : isLoading || requestsLoading ? (
        <Flex justify="center" py="20">
          <Spinner color="smPrimary" />
        </Flex>
      ) : bids.length === 0 && requestsWithoutBid.length === 0 && !requestsFailed ? (
        <EmptyCard text={t("You have not started any offers yet")} />
      ) : bids.length === 0 && requestsWithoutBid.length === 0 ? (
        <RequestsFailedAlert onRetry={() => refetchRequests()} />
      ) : (
        <>
          {requestsFailed && <RequestsFailedAlert onRetry={() => refetchRequests()} />}
          <Box mb="5">
            <LifecycleFilterChips active={filter} counts={lifecycleCounts} onChange={setFilter} />
          </Box>

          {visible.length === 0 && visibleRequests.length === 0 ? (
            <EmptyCard text={t("No offers match your current search")} />
          ) : (
            <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("Procurement")}</Th>
                    <Th>{t("Competition")}</Th>
                    <Th>{t("My bid")}</Th>
                    <Th>{t("Awarded to")}</Th>
                    <Th>{t("Deadline")}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleRequests.map((row) => {
                    const badge = participationRequestBadge(row, t, now);
                    return (
                      <Tr
                        key={row.request.id}
                        cursor="pointer"
                        _hover={{ bg: "smPrimarySofter" }}
                        onClick={() => openRequest(row)}
                      >
                        <Td>
                          <Text fontWeight="medium" color="smText">
                            {row.eventTitle || t("Untitled")}
                          </Text>
                          <Text fontSize="sm" color="smMuted">
                            {row.buyerOrganizationName}
                          </Text>
                        </Td>
                        <Td>
                          <Tag variant="outline" colorScheme="blue" size="sm">
                            {t("Qualification")}
                          </Tag>
                        </Td>
                        <Td>
                          <Tag colorScheme={badge.colorScheme} size="sm" borderRadius="full" whiteSpace="nowrap">
                            {badge.label}
                          </Tag>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="smMuted">
                            —
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="smMuted" whiteSpace="nowrap">
                            {row.requestDeadline ? displayDateWithTime(row.requestDeadline, null, locale) : "—"}
                          </Text>
                        </Td>
                        <Td textAlign="right" whiteSpace="nowrap">
                          <Icon as={FiChevronRight} color="smMuted" ml="3" />
                        </Td>
                      </Tr>
                    );
                  })}
                  {visible.map((bid) => (
                    <Tr key={bid.bidId} cursor="pointer" _hover={{ bg: "smPrimarySofter" }} onClick={() => openRow(bid)}>
                      <Td>
                        <Text fontWeight="medium" color="smText">
                          {bid.event.title || t("Untitled")}
                        </Text>
                        <Text fontSize="sm" color="smMuted">
                          {bid.event.buyerName}
                        </Text>
                      </Td>
                      <Td>
                        <PortalLifecycleTag
                          status={bid.event.lifecycle.status}
                          stage={bid.event.competitionStage}
                          procedureType={bid.event.procedureType}
                          designContest={isDesignContest(bid.event.procedureType)}
                          frameworkStopped={bid.event.frameworkAgreementNoLongerActive}
                        />
                      </Td>
                      <Td>
                        <BidStatusTag {...myBidTagProps(bid)} size="sm" />
                      </Td>
                      <Td>
                        <Text fontSize="sm" color={bid.awardOutcome ? "smText" : "smMuted"}>
                          {awardedToText(bid, t)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="smMuted" whiteSpace="nowrap">
                          {myBidDeadlineText(bid, locale)}
                        </Text>
                        {isCompetitionOpen(bid) && bid.event.bidSubmissionDeadline && (
                          <TimeLeft deadline={bid.event.bidSubmissionDeadline} />
                        )}
                      </Td>
                      <Td textAlign="right" whiteSpace="nowrap">
                        <Button
                          size="sm"
                          variant="link"
                          colorScheme="teal"
                          onClick={(e) => {
                            e.stopPropagation();
                            openBid(bid);
                          }}
                        >
                          {isOpenDraft(bid)
                            ? t("Continue")
                            : isDesignContest(bid.event.procedureType)
                              ? t("View entry")
                              : t("View offer")}
                        </Button>
                        <Icon as={FiChevronRight} color="smMuted" ml="3" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}
    </>
  );
};

const TimeLeft = ({ deadline }: { deadline: string }) => {
  const { t } = useTranslation();
  const { label, color } = getTimeLeftMeta(deadline, t);
  return (
    <Text fontSize="xs" color={color}>
      {label}
    </Text>
  );
};

const RequestsFailedAlert = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  return (
    <Alert status="error" rounded="md" mb="5">
      <AlertIcon />
      <AlertDescription flex="1">{t("Your requests to participate could not be loaded.")}</AlertDescription>
      <Button size="sm" variant="outline" onClick={onRetry}>
        {t("Try again")}
      </Button>
    </Alert>
  );
};

const EmptyCard = ({ text }: { text: string }) => (
  <Card variant="outline" rounded="xl">
    <CardBody py="20">
      <Flex align="center" justify="center">
        <Text color="smMuted">{text}</Text>
      </Flex>
    </CardBody>
  </Card>
);
